import { useState, useEffect, useCallback } from 'react'
import api from '../lib/api'
import { useEstablishment } from '../contexts/EstablishmentContext'
import { format, getISOWeek } from 'date-fns'
import { fr } from 'date-fns/locale'

// ─── Types ────────────────────────────────────────────────────────────────────

interface Product  { id: string; nom: string; poidsReglementaire: number; tolerance: number; actif: boolean; fournisseurId?: string; fournisseurNom?: string }
interface Supplier { id: string; nom: string; actif: boolean }
interface Control  {
  id?: string; date: string; heure: string; employeeName: string
  produit: string; fournisseur: string; quantite: number
  poidsTotal: number; seuilMini: number; resultat: string; type: string
}
interface Photo { id: string; date: string; heure: string; mois: string; employeeName: string; fileName: string }

// ─── Helper ───────────────────────────────────────────────────────────────────

function currentMonth() {
  const now = new Date()
  return `${now.getFullYear()}_${String(now.getMonth() + 1).padStart(2, '0')}`
}
function monthLabel(m: string) {
  const [y, mo] = m.split('_')
  return format(new Date(Number(y), Number(mo) - 1, 1), 'MMMM yyyy', { locale: fr })
}

export default function Bvp() {
  const [tab, setTab] = useState<'history' | 'photos' | 'products' | 'suppliers'>('history')
  const { current: establishment } = useEstablishment()
  const estId = establishment?.id

  // ── Produits ────────────────────────────────────────────────────────────────
  const [products, setProducts] = useState<Product[]>([])
  const [newProduct, setNewProduct] = useState({ nom: '', poidsReglementaire: '', tolerance: '5', fournisseurId: '', fournisseurNom: '' })
  const [editProduct, setEditProduct] = useState<Product | null>(null)

  const loadProducts = useCallback(async () => {
    if (!estId) return
    const r = await api.get(`/bvp/products?establishmentId=${estId}`)
    setProducts(r.data)
  }, [estId])

  useEffect(() => { if (tab === 'products') loadProducts() }, [tab, loadProducts])

  const saveProduct = async () => {
    if (!newProduct.nom || !newProduct.poidsReglementaire || !estId) return
    await api.post('/bvp/products', { ...newProduct, poidsReglementaire: Number(newProduct.poidsReglementaire), establishmentId: estId })
    setNewProduct({ nom: '', poidsReglementaire: '', tolerance: '5', fournisseurId: '', fournisseurNom: '' }); loadProducts()
  }

  const toggleProduct = async (p: Product) => {
    await api.patch(`/bvp/products/${p.id}`, { actif: !p.actif }); loadProducts()
  }

  const updateProduct = async () => {
    if (!editProduct) return
    await api.patch(`/bvp/products/${editProduct.id}`, { nom: editProduct.nom, poidsReglementaire: editProduct.poidsReglementaire, tolerance: editProduct.tolerance, fournisseurId: editProduct.fournisseurId || null, fournisseurNom: editProduct.fournisseurNom || null })
    setEditProduct(null); loadProducts()
  }

  // ── Fournisseurs ─────────────────────────────────────────────────────────────
  const [suppliers, setSuppliers] = useState<Supplier[]>([])
  const [newSupplier, setNewSupplier] = useState('')
  const [editSupplier, setEditSupplier] = useState<Supplier | null>(null)

  const loadSuppliers = useCallback(async () => {
    if (!estId) return
    const r = await api.get(`/bvp/suppliers?establishmentId=${estId}`)
    setSuppliers(r.data)
  }, [estId])

  useEffect(() => { if (tab === 'suppliers' || tab === 'products') loadSuppliers() }, [tab, loadSuppliers])

  const saveSupplier = async () => {
    if (!newSupplier || !estId) return
    await api.post('/bvp/suppliers', { nom: newSupplier, establishmentId: estId })
    setNewSupplier(''); loadSuppliers()
  }

  const toggleSupplier = async (s: Supplier) => {
    await api.patch(`/bvp/suppliers/${s.id}`, { actif: !s.actif }); loadSuppliers()
  }

  // ── Historique ───────────────────────────────────────────────────────────────
  const [month, setMonth] = useState(currentMonth())
  const [controls, setControls] = useState<Control[]>([])
  const [loadingControls, setLoadingControls] = useState(false)
  const [showRegul, setShowRegul] = useState(false)
  const [regulForm, setRegulForm] = useState({ employeeName: '', produit: '', fournisseur: '', quantite: '10', poidsTotal: '', poidsReglementaire: '' })
  const [regulLoading, setRegulLoading] = useState(false)
  const [exportLoading, setExportLoading] = useState<'excel' | 'pdf' | null>(null)

  // ── Photos ───────────────────────────────────────────────────────────────────
  const [photos, setPhotos] = useState<Photo[]>([])
  const [loadingPhotos, setLoadingPhotos] = useState(false)
  const [photoMois, setPhotoMois] = useState(currentMonth())
  const [previewPhoto, setPreviewPhoto] = useState<{ id: string; imageData: string; fileName: string } | null>(null)
  const [photoExportLoading, setPhotoExportLoading] = useState<string | null>(null)

  // Sélecteur semaine : 12 dernières semaines
  const weekOptions = Array.from({ length: 12 }, (_, i) => {
    const d = new Date(); d.setDate(d.getDate() - i * 7)
    const w = getISOWeek(d)
    const y = d.getFullYear()
    const mon = new Date(d); mon.setDate(d.getDate() - ((d.getDay() + 6) % 7))
    const sun = new Date(mon); sun.setDate(mon.getDate() + 6)
    const label = `Sem. ${w} — ${mon.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })} → ${sun.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })}`
    return { value: `${y}-W${String(w).padStart(2, '0')}`, label }
  })
  const [selectedWeek, setSelectedWeek] = useState(weekOptions[0].value)

  const loadPhotos = useCallback(async () => {
    if (!estId) return
    setLoadingPhotos(true)
    try {
      const r = await api.get(`/bvp/photos?establishmentId=${estId}&mois=${photoMois}`)
      setPhotos(r.data || [])
    } finally { setLoadingPhotos(false) }
  }, [estId, photoMois])

  useEffect(() => { if (tab === 'photos') loadPhotos() }, [tab, loadPhotos])

  const openPhoto = async (id: string) => {
    const r = await api.get(`/bvp/photos/${id}`)
    setPreviewPhoto({ id, imageData: r.data.imageData, fileName: r.data.fileName })
  }

  const deletePhoto = async (id: string) => {
    if (!confirm('Supprimer cette photo ?')) return
    await api.delete(`/bvp/photos/${id}`)
    setPreviewPhoto(null); loadPhotos()
  }

  const exportPhotosPdf = async (period: 'week' | 'month' | 'year') => {
    if (!estId) return
    const value = period === 'week' ? selectedWeek : period === 'month' ? photoMois : photoMois.split('_')[0]
    setPhotoExportLoading(period)
    try {
      const r = await api.get(`/bvp/photos/export/pdf?establishmentId=${estId}&period=${period}&value=${value}`, { responseType: 'blob' })
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }))
      const a = document.createElement('a'); a.href = url; a.download = `etiquettes_${value}.pdf`
      document.body.appendChild(a); a.click(); document.body.removeChild(a)
    } finally { setPhotoExportLoading(null) }
  }

  const loadControls = useCallback(async () => {
    if (!estId) return
    setLoadingControls(true)
    try {
      const r = await api.get(`/bvp/controls?establishmentId=${estId}&month=${month}`)
      // Build full month with missing days client-side for display
      setControls(buildFullMonth(r.data, month))
    } finally { setLoadingControls(false) }
  }, [estId, month])

  useEffect(() => { if (tab === 'history') loadControls() }, [tab, loadControls])

  const exportFile = async (type: 'excel' | 'pdf') => {
    if (!estId) return
    setExportLoading(type)
    try {
      const ext = type === 'excel' ? 'xlsx' : 'pdf'
      const mime = type === 'excel'
        ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        : 'application/pdf'
      const r = await api.get(`/bvp/controls/export/${type}?establishmentId=${estId}&month=${month}`, { responseType: 'blob' })
      const url = URL.createObjectURL(new Blob([r.data], { type: mime }))
      const a = document.createElement('a')
      a.href = url; a.download = `historique_bvp_${month}.${ext}`
      document.body.appendChild(a); a.click(); document.body.removeChild(a)
    } finally { setExportLoading(null) }
  }

  const runRegularization = async () => {
    if (!estId || !regulForm.employeeName || !regulForm.produit || !regulForm.fournisseur || !regulForm.poidsTotal || !regulForm.poidsReglementaire) return
    setRegulLoading(true)
    try {
      const r = await api.post('/bvp/controls/regularization-bulk', {
        establishmentId: estId, month,
        ...regulForm,
        quantite: Number(regulForm.quantite),
        poidsTotal: Number(regulForm.poidsTotal),
        poidsReglementaire: Number(regulForm.poidsReglementaire)
      })
      alert(`✅ ${r.data.created} jours régularisés`)
      setShowRegul(false); loadControls()
    } catch { alert('Erreur lors de la régularisation') }
    finally { setRegulLoading(false) }
  }

  // ─── Months list (12 derniers mois) ──────────────────────────────────────────
  const months = Array.from({ length: 12 }, (_, i) => {
    const d = new Date(); d.setDate(1); d.setMonth(d.getMonth() - i)
    return `${d.getFullYear()}_${String(d.getMonth() + 1).padStart(2, '0')}`
  })

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">BVP — Contrôle des poids</h1>
          <p className="text-sm text-gray-500 mt-1">Gestion des contrôles réglementaires</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 border-b">
        {[
          { key: 'history', label: 'Historique' },
          { key: 'photos', label: 'Photos étiquettes' },
          { key: 'products', label: 'Produits' },
          { key: 'suppliers', label: 'Fournisseurs' },
        ].map(t => (
          <button key={t.key} onClick={() => setTab(t.key as any)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${tab === t.key ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ── HISTORIQUE ──────────────────────────────────────────────────────────── */}
      {tab === 'history' && (
        <div>
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <select value={month} onChange={e => setMonth(e.target.value)}
              className="border rounded-lg px-3 py-2 text-sm">
              {months.map(m => <option key={m} value={m}>{monthLabel(m)}</option>)}
            </select>
            <button onClick={() => exportFile('excel')} disabled={exportLoading === 'excel'}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-60">
              {exportLoading === 'excel' ? '...' : '📊 Excel'}
            </button>
            <button onClick={() => exportFile('pdf')} disabled={exportLoading === 'pdf'}
              className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 disabled:opacity-60">
              {exportLoading === 'pdf' ? '...' : '📄 PDF'}
            </button>
            <button onClick={() => setShowRegul(true)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700">
              🔄 Régulariser les jours manquants
            </button>
          </div>

          {loadingControls ? (
            <div className="text-center py-12 text-gray-400">Chargement...</div>
          ) : (
            <div className="bg-white rounded-xl border overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-600 font-semibold">
                  <tr>
                    {['Date', 'Heure', 'Employé', 'Produit', 'Fournisseur', 'Qté', 'Poids (g)', 'Seuil (g)', 'Résultat', 'Type'].map(h => (
                      <th key={h} className="px-3 py-3 text-left text-xs">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {controls.map((c, i) => (
                    <tr key={i} className={c.type === 'MANQUANT' ? 'bg-orange-50' : c.type === 'REGULARISATION' ? 'bg-blue-50' : ''}>
                      <td className="px-3 py-2 text-xs">{c.date}</td>
                      <td className="px-3 py-2 text-xs">{c.heure}</td>
                      <td className="px-3 py-2 text-xs">{c.employeeName}</td>
                      <td className="px-3 py-2 text-xs font-medium">{c.produit}</td>
                      <td className="px-3 py-2 text-xs">{c.fournisseur}</td>
                      <td className="px-3 py-2 text-xs text-center">{c.quantite || '—'}</td>
                      <td className="px-3 py-2 text-xs text-center">{c.poidsTotal || '—'}</td>
                      <td className="px-3 py-2 text-xs text-center">{c.seuilMini || '—'}</td>
                      <td className="px-3 py-2 text-xs font-bold">
                        {c.resultat === 'CONFORME' && <span className="text-green-600">✓ CONFORME</span>}
                        {c.resultat === 'NON_CONFORME' && <span className="text-red-600">✗ NON CONFORME</span>}
                        {c.resultat === 'MANQUANT' && <span className="text-orange-500">— MANQUANT</span>}
                      </td>
                      <td className="px-3 py-2 text-xs">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          c.type === 'MANQUANT' ? 'bg-orange-100 text-orange-700' :
                          c.type === 'REGULARISATION' ? 'bg-blue-100 text-blue-700' :
                          'bg-gray-100 text-gray-600'}`}>
                          {c.type}
                        </span>
                      </td>
                    </tr>
                  ))}
                  {controls.length === 0 && (
                    <tr><td colSpan={10} className="text-center py-8 text-gray-400">Aucun contrôle ce mois</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* Modal régularisation */}
          {showRegul && (
            <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
              <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
                <h2 className="font-bold text-lg mb-4">Régularisation — {monthLabel(month)}</h2>
                <p className="text-sm text-gray-500 mb-4">Crée une ligne par jour manquant avec les données ci-dessous. Ne touche jamais aux contrôles réels.</p>
                <div className="space-y-3">
                  {[
                    { label: 'Employé', key: 'employeeName', type: 'text' },
                    { label: 'Produit', key: 'produit', type: 'text' },
                    { label: 'Fournisseur', key: 'fournisseur', type: 'text' },
                    { label: 'Poids réglementaire (g)', key: 'poidsReglementaire', type: 'number' },
                    { label: 'Quantité pesée', key: 'quantite', type: 'number' },
                    { label: 'Poids total (g)', key: 'poidsTotal', type: 'number' },
                  ].map(f => (
                    <div key={f.key}>
                      <label className="text-xs text-gray-500">{f.label}</label>
                      <input type={f.type} value={(regulForm as any)[f.key]}
                        onChange={e => setRegulForm(p => ({ ...p, [f.key]: e.target.value }))}
                        className="w-full border rounded-lg px-3 py-2 text-sm mt-1" />
                    </div>
                  ))}
                </div>
                <div className="flex gap-3 mt-5">
                  <button onClick={() => setShowRegul(false)} className="flex-1 border rounded-lg py-2 text-sm">Annuler</button>
                  <button onClick={runRegularization} disabled={regulLoading}
                    className="flex-1 bg-blue-600 text-white rounded-lg py-2 text-sm font-semibold disabled:opacity-60">
                    {regulLoading ? 'En cours...' : 'Régulariser'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── PHOTOS ÉTIQUETTES ───────────────────────────────────────────────────── */}
      {tab === 'photos' && (
        <div>
          <div className="flex flex-wrap items-end gap-3 mb-4">
            <div>
              <label className="text-xs text-gray-500 block mb-1">Mois affiché</label>
              <select value={photoMois} onChange={e => setPhotoMois(e.target.value)}
                className="border rounded-lg px-3 py-2 text-sm">
                {months.map(m => <option key={m} value={m}>{monthLabel(m)}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-gray-500 block mb-1">Semaine (export)</label>
              <select value={selectedWeek} onChange={e => setSelectedWeek(e.target.value)}
                className="border rounded-lg px-3 py-2 text-sm">
                {weekOptions.map(w => <option key={w.value} value={w.value}>{w.label}</option>)}
              </select>
            </div>
            <span className="text-sm text-gray-500 self-center">{photos.length} photo(s)</span>
            <div className="flex gap-2 ml-auto">
              <button onClick={() => exportPhotosPdf('week')} disabled={photoExportLoading === 'week'}
                className="px-3 py-2 bg-indigo-600 text-white rounded-lg text-xs font-medium hover:bg-indigo-700 disabled:opacity-60">
                {photoExportLoading === 'week' ? '...' : '📄 PDF semaine'}
              </button>
              <button onClick={() => exportPhotosPdf('month')} disabled={photoExportLoading === 'month'}
                className="px-3 py-2 bg-red-600 text-white rounded-lg text-xs font-medium hover:bg-red-700 disabled:opacity-60">
                {photoExportLoading === 'month' ? '...' : '📄 PDF mois'}
              </button>
              <button onClick={() => exportPhotosPdf('year')} disabled={photoExportLoading === 'year'}
                className="px-3 py-2 bg-orange-600 text-white rounded-lg text-xs font-medium hover:bg-orange-700 disabled:opacity-60">
                {photoExportLoading === 'year' ? '...' : '📄 PDF année'}
              </button>
            </div>
          </div>

          {loadingPhotos ? (
            <div className="text-center py-12 text-gray-400">Chargement...</div>
          ) : photos.length === 0 ? (
            <div className="text-center py-12 text-gray-400">Aucune photo ce mois</div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {photos.map(p => (
                <div key={p.id} className="bg-white rounded-xl border border-gray-200 p-3 hover:border-blue-300 transition-colors">
                  <button onClick={() => openPhoto(p.id)} className="w-full text-left">
                    <div className="w-full h-32 bg-gray-100 rounded-lg flex items-center justify-center mb-2 overflow-hidden">
                      <span className="text-4xl">🖼️</span>
                    </div>
                    <p className="text-xs font-semibold text-gray-800">{p.date} {p.heure}</p>
                    <p className="text-xs text-gray-500 truncate mb-2">{p.employeeName}</p>
                  </button>
                  <button onClick={() => deletePhoto(p.id)}
                    className="w-full text-xs text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg py-1.5 font-medium transition-colors border border-red-100">
                    Supprimer
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Modal prévisualisation */}
          {previewPhoto && (
            <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setPreviewPhoto(null)}>
              <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full overflow-hidden" onClick={e => e.stopPropagation()}>
                <div className="flex items-center justify-between px-4 py-3 border-b">
                  <p className="font-semibold text-sm text-gray-800 truncate flex-1">{previewPhoto.fileName}</p>
                  <div className="flex items-center gap-2 ml-3">
                    <button onClick={() => deletePhoto(previewPhoto.id)}
                      className="px-3 py-1.5 bg-red-100 text-red-600 rounded-lg text-xs font-medium hover:bg-red-200">
                      Supprimer
                    </button>
                    <button onClick={() => setPreviewPhoto(null)}
                      className="px-3 py-1.5 bg-gray-100 text-gray-600 rounded-lg text-xs font-medium hover:bg-gray-200">
                      Fermer
                    </button>
                  </div>
                </div>
                <div className="p-4 flex items-center justify-center bg-gray-50">
                  <img src={`data:image/jpeg;base64,${previewPhoto.imageData}`}
                    alt="Étiquette carton"
                    className="max-w-full max-h-[60vh] object-contain rounded-lg" />
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── PRODUITS ────────────────────────────────────────────────────────────── */}
      {tab === 'products' && (
        <div>
          <div className="bg-white rounded-xl border p-4 mb-4">
            <h3 className="font-semibold mb-3">Ajouter un produit</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Nom du produit</label>
                <input placeholder="ex: Rustiguette" value={newProduct.nom}
                  onChange={e => setNewProduct(p => ({ ...p, nom: e.target.value }))}
                  className="w-full border rounded-lg px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Poids réglementaire (g)</label>
                <input placeholder="ex: 250" type="number" value={newProduct.poidsReglementaire}
                  onChange={e => setNewProduct(p => ({ ...p, poidsReglementaire: e.target.value }))}
                  className="w-full border rounded-lg px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Tolérance (g/unité)</label>
                <input placeholder="ex: 5" type="number" value={newProduct.tolerance}
                  onChange={e => setNewProduct(p => ({ ...p, tolerance: e.target.value }))}
                  className="w-full border rounded-lg px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Fournisseur</label>
                <select value={newProduct.fournisseurId}
                  onChange={e => {
                    const s = suppliers.find(s => s.id === e.target.value)
                    setNewProduct(p => ({ ...p, fournisseurId: e.target.value, fournisseurNom: s?.nom || '' }))
                  }}
                  className="w-full border rounded-lg px-3 py-2 text-sm">
                  <option value="">— Sélectionner —</option>
                  {suppliers.filter(s => s.actif).map(s => <option key={s.id} value={s.id}>{s.nom}</option>)}
                </select>
              </div>
              <div className="flex items-end">
                <button onClick={saveProduct} className="w-full bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700">
                  Ajouter
                </button>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl border overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50"><tr>
                <th className="px-4 py-3 text-left font-semibold text-gray-600">Produit</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-600">Fournisseur</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-600">Poids réglementaire</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-600">Tolérance</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-600">Statut</th>
                <th className="px-4 py-3"></th>
              </tr></thead>
              <tbody className="divide-y">
                {products.map(p => (
                  <tr key={p.id} className={!p.actif ? 'opacity-40' : ''}>
                    <td className="px-4 py-3 font-medium">
                      {editProduct?.id === p.id
                        ? <input value={editProduct.nom} onChange={e => setEditProduct({ ...editProduct, nom: e.target.value })}
                            className="border rounded px-2 py-1 text-sm w-full" />
                        : p.nom}
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      {editProduct?.id === p.id
                        ? <select value={editProduct.fournisseurId || ''}
                            onChange={e => {
                              const s = suppliers.find(s => s.id === e.target.value)
                              setEditProduct({ ...editProduct, fournisseurId: e.target.value, fournisseurNom: s?.nom || '' })
                            }}
                            className="border rounded px-2 py-1 text-sm">
                            <option value="">— Aucun —</option>
                            {suppliers.filter(s => s.actif).map(s => <option key={s.id} value={s.id}>{s.nom}</option>)}
                          </select>
                        : p.fournisseurNom || <span className="text-gray-300 italic">—</span>}
                    </td>
                    <td className="px-4 py-3">
                      {editProduct?.id === p.id
                        ? <input type="number" value={editProduct.poidsReglementaire}
                            onChange={e => setEditProduct({ ...editProduct, poidsReglementaire: Number(e.target.value) })}
                            className="border rounded px-2 py-1 text-sm w-24" />
                        : `${p.poidsReglementaire} g`}
                    </td>
                    <td className="px-4 py-3 text-gray-500">
                      {editProduct?.id === p.id
                        ? <input type="number" value={editProduct.tolerance}
                            onChange={e => setEditProduct({ ...editProduct, tolerance: Number(e.target.value) })}
                            className="border rounded px-2 py-1 text-sm w-20" />
                        : `±${p.tolerance ?? 5} g/unité`}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${p.actif ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                        {p.actif ? 'Actif' : 'Inactif'}
                      </span>
                    </td>
                    <td className="px-4 py-3 flex items-center gap-2">
                      {editProduct?.id === p.id
                        ? <>
                            <button onClick={updateProduct} className="text-xs text-green-600 font-medium hover:underline">Enregistrer</button>
                            <button onClick={() => setEditProduct(null)} className="text-xs text-gray-400 hover:underline">Annuler</button>
                          </>
                        : <>
                            <button onClick={() => setEditProduct(p)} className="text-xs text-blue-600 hover:underline">Modifier</button>
                            <button onClick={() => toggleProduct(p)} className="text-xs text-gray-500 hover:underline">
                              {p.actif ? 'Désactiver' : 'Activer'}
                            </button>
                          </>
                      }
                    </td>
                  </tr>
                ))}
                {products.length === 0 && (
                  <tr><td colSpan={5} className="text-center py-8 text-gray-400">Aucun produit</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── FOURNISSEURS ────────────────────────────────────────────────────────── */}
      {tab === 'suppliers' && (
        <div>
          <div className="bg-white rounded-xl border p-4 mb-4">
            <h3 className="font-semibold mb-3">Ajouter un fournisseur</h3>
            <div className="flex gap-3">
              <input placeholder="Nom du fournisseur (ex: Marie Blachère)" value={newSupplier}
                onChange={e => setNewSupplier(e.target.value)}
                className="flex-1 border rounded-lg px-3 py-2 text-sm" />
              <button onClick={saveSupplier} className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700">
                Ajouter
              </button>
            </div>
          </div>

          <div className="bg-white rounded-xl border overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50"><tr>
                <th className="px-4 py-3 text-left font-semibold text-gray-600">Fournisseur</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-600">Statut</th>
                <th className="px-4 py-3"></th>
              </tr></thead>
              <tbody className="divide-y">
                {suppliers.map(s => (
                  <tr key={s.id} className={!s.actif ? 'opacity-40' : ''}>
                    <td className="px-4 py-3 font-medium">
                      {editSupplier?.id === s.id
                        ? <input value={editSupplier.nom} onChange={e => setEditSupplier({ ...editSupplier, nom: e.target.value })}
                            className="border rounded px-2 py-1 text-sm w-full" />
                        : s.nom}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${s.actif ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                        {s.actif ? 'Actif' : 'Inactif'}
                      </span>
                    </td>
                    <td className="px-4 py-3 flex items-center gap-2">
                      {editSupplier?.id === s.id
                        ? <>
                            <button onClick={async () => { await api.patch(`/bvp/suppliers/${s.id}`, { nom: editSupplier.nom }); setEditSupplier(null); loadSuppliers() }}
                              className="text-xs text-green-600 font-medium hover:underline">Enregistrer</button>
                            <button onClick={() => setEditSupplier(null)} className="text-xs text-gray-400 hover:underline">Annuler</button>
                          </>
                        : <>
                            <button onClick={() => setEditSupplier(s)} className="text-xs text-blue-600 hover:underline">Modifier</button>
                            <button onClick={() => toggleSupplier(s)} className="text-xs text-gray-500 hover:underline">
                              {s.actif ? 'Désactiver' : 'Activer'}
                            </button>
                          </>
                      }
                    </td>
                  </tr>
                ))}
                {suppliers.length === 0 && (
                  <tr><td colSpan={3} className="text-center py-8 text-gray-400">Aucun fournisseur</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}

// ─── Build full month client side ────────────────────────────────────────────

function buildFullMonth(controls: Control[], month: string): Control[] {
  const [yyyy, mm] = month.split('_').map(Number)
  const daysInMonth = new Date(yyyy, mm, 0).getDate()
  const datesWithControl = new Set(controls.map(c => c.date))
  const result = [...controls]
  for (let d = 1; d <= daysInMonth; d++) {
    const dateStr = `${String(d).padStart(2, '0')}/${String(mm).padStart(2, '0')}/${yyyy}`
    if (!datesWithControl.has(dateStr)) {
      result.push({ date: dateStr, heure: '', employeeName: '—', produit: 'AUCUN CONTRÔLE', fournisseur: '—', quantite: 0, poidsTotal: 0, seuilMini: 0, resultat: 'MANQUANT', type: 'MANQUANT' })
    }
  }
  result.sort((a, b) => {
    const toTs = (s: string) => { const [d, m, y] = s.split('/').map(Number); return new Date(y, m - 1, d).getTime() }
    return toTs(a.date) - toTs(b.date)
  })
  return result
}
